import { Box, FormSelect, FormText, IconButton, Modal, ModalDialog, PrimaryButton, SecondaryButton, Typography } from "@husqvarna/ui-core";
import { FormSelectOption } from "@husqvarna/ui-core/dist/components/form-select";
import { FormattedMessage, useIntl } from "react-intl";
import { useForm } from "react-hook-form";
import { useEffect, useRef, useState } from "react";
import { useCreateRegionMutation, useLazyGetAdminProductsQuery, useSaveAdminProductsMutation } from "../adminApi";
import { UnitSystem } from "../../project/models/Enums";
import { AdminProduct } from "../models/AdminProduct";
import Region from "../../shared/models/Region";

interface CreateRegionFormValues {
    name: string;
    countryCode: string;
    cultureCode: string;
    currency: string;
    unitSystem: string;
    emissionFactor: string;
}

interface CreateRegionDialogProps {
    onClose: () => void;
    onCreated: (regionId: string) => void;
    regions?: Region[];
}

const CreateRegionDialog = ({ onClose, onCreated, regions }: CreateRegionDialogProps) => {
    const intl = useIntl();
    const [createRegion, { isLoading }] = useCreateRegionMutation();
    const [saveProducts] = useSaveAdminProductsMutation();
    const [fetchProducts] = useLazyGetAdminProductsQuery();
    const [serverError, setServerError] = useState<string | null>(null);
    const [copyFromRegionId, setCopyFromRegionId] = useState<string>("");
    const [copiedProducts, setCopiedProducts] = useState<AdminProduct[] | null>(null);
    const formRef = useRef<HTMLFormElement>(null);

    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [onClose]);

    const { register, handleSubmit, watch, setValue, reset, getValues, formState: { errors } } = useForm<CreateRegionFormValues>({
        defaultValues: {
            name: "",
            countryCode: "",
            cultureCode: "",
            currency: "",
            unitSystem: UnitSystem.Metric,
            emissionFactor: ""
        }
    });

    const unitSystemOptions: FormSelectOption[] = [
        { label: "Metric", value: UnitSystem.Metric },
        { label: "Imperial", value: UnitSystem.Imperial }
    ];

    const regionOptions: FormSelectOption[] = (regions ?? []).map(r => ({
        label: r.name,
        value: r.id
    }));

    const triggerKeyUpOnInputs = () => {
        if (!formRef.current) return;
        formRef.current.querySelectorAll('input').forEach(input => {
            input.dispatchEvent(new KeyboardEvent('keyup', { bubbles: true }));
        });
    };

    const handleCopyFrom = async () => {
        if (!copyFromRegionId) return;
        const sourceRegion = regions?.find(r => r.id === copyFromRegionId);
        if (sourceRegion) {
            reset({
                ...getValues(),
                currency: sourceRegion.currency,
                unitSystem: sourceRegion.unitSystem,
                cultureCode: sourceRegion.cultureCode ?? "",
                emissionFactor: sourceRegion.emissionFactor?.toString() ?? ""
            });
            setTimeout(triggerKeyUpOnInputs, 50);
        }
        const result = await fetchProducts(copyFromRegionId);
        if (result.data) {
            setCopiedProducts(result.data);
        }
    };

    const onSubmit = async (data: CreateRegionFormValues) => {
        setServerError(null);
        try {
            const result = await createRegion({
                name: data.name,
                countryCode: data.countryCode,
                cultureCode: data.cultureCode,
                currency: data.currency,
                unitSystem: data.unitSystem === UnitSystem.Imperial ? 1 : 0,
                emissionFactor: data.emissionFactor ? parseFloat(data.emissionFactor) : null
            }).unwrap();

            if (copiedProducts?.length) {
                await saveProducts({
                    regionId: result.id,
                    products: copiedProducts.map(p => ({
                        productId: p.productId,
                        isActive: p.isActive,
                        price: p.price
                    }))
                }).unwrap();
            }

            onCreated(result.id);
        } catch (err: any) {
            const message = typeof err?.data === 'string' ? err.data : "An error occurred.";
            setServerError(message);
        }
    };

    return (
        <Modal pageScrollLock closeable={false} active onClosing={onClose}>
            <ModalDialog br={6} className="admin-create-region-dialog">
                <IconButton className="tcsp-project__dialog-close" icon="close" onClick={onClose} />
                <Box mx={10} mt={10} mb={6} flexDirection="column">
                    <Typography variant="Heading4" mb={7}>
                        <FormattedMessage id="admin.createRegionDialog.title" defaultMessage="Create new region" />
                    </Typography>

                    {regions && regions.length > 0 && (
                        <Box mb={5} alignItems="flex-end" gap={3}>
                            <div style={{ width: '300px' }}>
                                <FormSelect
                                    width="auto"
                                    label={intl.formatMessage({ id: "admin.createRegionDialog.copyFrom", defaultMessage: "Copy values from" })}
                                    options={[{ label: "— Select region —", value: "" }, ...regionOptions]}
                                    selectedValue={copyFromRegionId}
                                    onChangeListener={(e: any) => { setCopyFromRegionId(e.target.value); setCopiedProducts(null); }}
                                />
                            </div>
                            <SecondaryButton
                                text={intl.formatMessage({ id: "admin.createRegionDialog.copyButton", defaultMessage: "Copy" })}
                                onClick={handleCopyFrom}
                                disabled={!copyFromRegionId}
                            />
                        </Box>
                    )}

                    {copiedProducts && (
                        <Box mt={4} mb={4}>
                            <Typography variant="Microcopy" color="Grey500">
                                {intl.formatMessage(
                                    { id: "admin.createRegionDialog.copiedInfo", defaultMessage: "Will copy {count} product prices. Please validate before saving!" },
                                    { count: copiedProducts.filter(p => p.price > 0).length }
                                )}
                            </Typography>
                        </Box>
                    )}

                    <form ref={formRef} onSubmit={handleSubmit(onSubmit)}>
                        <Box flexDirection="column" gap={3}>
                            <FormText
                                label={intl.formatMessage({ id: "admin.createRegionDialog.name", defaultMessage: "Name" })}
                                {...register("name", {
                                    required: intl.formatMessage({ id: "admin.createRegionDialog.nameRequired", defaultMessage: "Name is required" })
                                })}
                                error={errors.name?.message}
                            />
                            <FormText
                                label={intl.formatMessage({ id: "admin.createRegionDialog.countryCode", defaultMessage: "Country code" })}
                                {...register("countryCode", {
                                    required: intl.formatMessage({ id: "admin.createRegionDialog.countryCodeRequired", defaultMessage: "Country code is required" }),
                                    pattern: {
                                        value: /^[A-Z]{2}$/,
                                        message: intl.formatMessage({ id: "admin.createRegionDialog.countryCodeInvalid", defaultMessage: "Must be 2 letters (e.g. SE)" })
                                    }
                                })}
                                error={errors.countryCode?.message || undefined}
                            />
                            <FormText
                                label={intl.formatMessage({ id: "admin.createRegionDialog.cultureCode", defaultMessage: "Culture code" })}
                                {...register("cultureCode", {
                                    required: intl.formatMessage({ id: "admin.createRegionDialog.cultureCodeRequired", defaultMessage: "Culture code is required" }),
                                    pattern: {
                                        value: /^[a-z]{2}-[A-Z]{2}$/,
                                        message: intl.formatMessage({ id: "admin.createRegionDialog.cultureCodeInvalid", defaultMessage: "Must be format xx-XX (e.g. sv-SE)" })
                                    }
                                })}
                                error={errors.cultureCode?.message}
                            />
                            <FormText
                                label={intl.formatMessage({ id: "admin.createRegionDialog.currency", defaultMessage: "Currency" })}
                                {...register("currency", {
                                    required: intl.formatMessage({ id: "admin.createRegionDialog.currencyRequired", defaultMessage: "Currency is required" }),
                                    pattern: {
                                        value: /^[A-Z]{3}$/,
                                        message: intl.formatMessage({ id: "admin.createRegionDialog.currencyInvalid", defaultMessage: "Must be a 3-letter code (e.g. SEK)" })
                                    }
                                })}
                                error={errors.currency?.message}
                            />
                            <FormSelect
                                label={intl.formatMessage({ id: "admin.createRegionDialog.unitSystem", defaultMessage: "Unit system" })}
                                options={unitSystemOptions}
                                selectedValue={watch("unitSystem")}
                                {...register("unitSystem")}
                            />
                            <FormText
                                label={intl.formatMessage({ id: "admin.createRegionDialog.emissionFactor", defaultMessage: "Emission factor" })}
                                {...register("emissionFactor")}
                            />
                        </Box>

                        {serverError && (
                            <Box mt={4}>
                                <span style={{ color: '#d32f2f', fontSize: '14px' }}>{serverError}</span>
                            </Box>
                        )}

                        <Box mt={7} justifyContent="flex-end" gap={3}>
                            <SecondaryButton
                                text={intl.formatMessage({ id: "general.cancel", defaultMessage: "Cancel" })}
                                onClick={onClose}
                            />
                            <PrimaryButton
                                submit
                                text={intl.formatMessage({ id: "admin.createRegionDialog.create", defaultMessage: "Create" })}
                                disabled={isLoading}
                            />
                        </Box>
                    </form>
                </Box>
            </ModalDialog>
        </Modal>
    );
};

export default CreateRegionDialog;
